import React, { useState, useEffect, useCallback } from 'react';
import { GripVertical, Plus, Trash2, Layers } from 'lucide-react';
import {
  getProductOptionGroupList,
  type ProductOptionGroupListItem,
  type ProductOptionGroupListRequest,
} from '../../../utils/productOptionGroup';
import { generateAfter } from '../../../utils/fractionalIndex';
import type { ProductMappedOptionGroupLine } from './productOptionGroupFormTypes';

function recomputeSortOrders(lines: ProductMappedOptionGroupLine[]): ProductMappedOptionGroupLine[] {
  let prev: string | null = null;
  return lines.map((line) => {
    const sortOrder = generateAfter(prev);
    prev = sortOrder;
    return { ...line, sortOrder };
  });
}

function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return arr;
  const next = [...arr];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'ACTIVE':
      return '활성';
    case 'INACTIVE':
      return '비활성';
    case 'PENDING':
      return '대기중';
    case 'TERMINATED':
      return '종료';
    default:
      return status || '-';
  }
}

export interface ProductMappedOptionGroupsTabProps {
  projectCode: string;
  brandCode: string;
  brandName?: string;
  lines: ProductMappedOptionGroupLine[];
  onLinesChange: (lines: ProductMappedOptionGroupLine[]) => void;
}

const ProductMappedOptionGroupsTab: React.FC<ProductMappedOptionGroupsTabProps> = ({
  projectCode,
  brandCode,
  brandName,
  lines,
  onLinesChange,
}) => {
  const [candidates, setCandidates] = useState<ProductOptionGroupListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    if (!projectCode || !brandCode) {
      setCandidates([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const req: ProductOptionGroupListRequest = {
          page: 0,
          size: 500,
          sort: 'createdAt,desc',
          status: 'ACTIVE',
          brandCode,
        };
        const res = await getProductOptionGroupList(projectCode, req);
        if (!cancelled) setCandidates(res.groups ?? []);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : '옵션 그룹 목록을 불러오지 못했습니다.');
          setCandidates([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectCode, brandCode]);

  const selectedCodes = new Set(lines.map((l) => l.groupCode));

  const normalizedFilter = filterText.trim().toLowerCase();
  const filteredCandidates =
    normalizedFilter.length === 0
      ? candidates
      : candidates.filter((g) => {
          const code = String(g.code ?? '').toLowerCase();
          const name = String(g.name ?? '').toLowerCase();
          return code.includes(normalizedFilter) || name.includes(normalizedFilter);
        });

  const addGroup = (g: ProductOptionGroupListItem) => {
    if (selectedCodes.has(g.code)) return;
    const lastSo = lines.length ? lines[lines.length - 1].sortOrder : null;
    const sortOrder = generateAfter(lastSo);
    const row: ProductMappedOptionGroupLine = {
      groupCode: g.code,
      groupName: g.name,
      sortOrder,
      status: g.status,
      itemProductCount: g.itemProductCount,
      mappingProductCount: g.mappingProductCount,
    };
    onLinesChange([...lines, row]);
  };

  const removeAt = (index: number) => {
    const next = lines.filter((_, i) => i !== index);
    onLinesChange(recomputeSortOrders(next));
  };

  const onDragStart = (index: number) => (e: React.DragEvent) => {
    setDragIndex(index);
    e.dataTransfer.setData('text/plain', String(index));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = useCallback(
    (dropIndex: number) => (e: React.DragEvent) => {
      e.preventDefault();
      const from = Number(e.dataTransfer.getData('text/plain'));
      if (Number.isNaN(from) || from === dropIndex) {
        setDragIndex(null);
        return;
      }
      const moved = moveItem(lines, from, dropIndex);
      onLinesChange(recomputeSortOrders(moved));
      setDragIndex(null);
    },
    [lines, onLinesChange]
  );

  const brandDisplay = brandName?.trim() ? `${brandName} (${brandCode})` : brandCode;

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Layers className="w-5 h-5" />
          {brandDisplay} 옵션 그룹에서 추가
        </h3>
        <p className="text-sm text-gray-500 mb-3">선택한 브랜드의 옵션 그룹만 표시됩니다.</p>
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1">
            <input
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="코드 또는 옵션 그룹명으로 검색"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          <div className="flex items-center justify-between gap-3 sm:justify-end">
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {filteredCandidates.length}/{candidates.length}건
            </span>
            {filterText.trim() !== '' && (
              <button
                type="button"
                onClick={() => setFilterText('')}
                className="px-3 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                초기화
              </button>
            )}
          </div>
        </div>
        {!brandCode ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
            상품에 브랜드가 없으면 옵션 그룹을 추가할 수 없습니다.
          </div>
        ) : (
          <>
            {loading && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            )}
            {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
            {!loading && !error && (
              <div className="border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-600">코드</th>
                      <th className="px-3 py-2 text-left text-gray-600">옵션 그룹명</th>
                      <th className="px-3 py-2 text-center text-gray-600">상태</th>
                      <th className="px-3 py-2 text-right text-gray-600">옵션 품목 수</th>
                      <th className="px-3 py-2 text-right text-gray-600">매핑 상품 수</th>
                      <th className="px-3 py-2 text-right w-24">—</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {filteredCandidates.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                          표시할 옵션 그룹이 없습니다.
                        </td>
                      </tr>
                    ) : (
                      filteredCandidates.map((g) => (
                        <tr key={g.code} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono text-gray-800">{g.code}</td>
                          <td className="px-3 py-2 text-gray-900">{g.name}</td>
                          <td className="px-3 py-2 text-center text-gray-700">{getStatusLabel(g.status)}</td>
                          <td className="px-3 py-2 text-right text-gray-700">
                            {Number(g.itemProductCount ?? 0).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700">
                            {Number(g.mappingProductCount ?? 0).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              disabled={selectedCodes.has(g.code)}
                              onClick={() => addGroup(g)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Plus className="w-3 h-3" />
                              추가
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-2">매핑 옵션 그룹</h3>
        <p className="text-sm text-gray-500 mb-3">정렬 순서는 드래그로 순서 변경이 가능합니다.</p>
        <div className="border border-gray-200 rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-10 px-2 py-2" />
                <th className="px-2 py-2 text-left text-gray-600">옵션 그룹</th>
                <th className="px-2 py-2 text-center text-gray-600">상태</th>
                <th className="px-2 py-2 text-right text-gray-600">옵션 품목 수</th>
                <th className="px-2 py-2 text-right text-gray-600">매핑 상품 수</th>
                <th className="w-12 px-2 py-2" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {lines.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    위 목록에서 옵션 그룹을 추가하세요.
                  </td>
                </tr>
              ) : (
                lines.map((line, index) => {
                  const meta = candidates.find((c) => c.code === line.groupCode);
                  const statusRaw = line.status ?? meta?.status ?? '';
                  const itemCnt = line.itemProductCount ?? meta?.itemProductCount;
                  const mapCnt = line.mappingProductCount ?? meta?.mappingProductCount;
                  return (
                    <tr
                      key={line.groupCode}
                      draggable
                      onDragStart={onDragStart(index)}
                      onDragOver={onDragOver}
                      onDrop={onDrop(index)}
                      className={`hover:bg-gray-50 ${dragIndex === index ? 'opacity-70' : ''}`}
                    >
                      <td className="px-2 py-2 cursor-grab active:cursor-grabbing text-gray-400">
                        <GripVertical className="w-5 h-5" />
                      </td>
                      <td className="px-2 py-2">
                        <div className="font-medium text-gray-900">{line.groupName}</div>
                        <div className="text-xs text-gray-500 font-mono">{line.groupCode}</div>
                      </td>
                      <td className="px-2 py-2 text-center text-gray-700">
                        {statusRaw ? getStatusLabel(statusRaw) : '—'}
                      </td>
                      <td className="px-2 py-2 text-right text-gray-700">
                        {itemCnt != null && !Number.isNaN(Number(itemCnt))
                          ? Number(itemCnt).toLocaleString()
                          : '—'}
                      </td>
                      <td className="px-2 py-2 text-right text-gray-700">
                        {mapCnt != null && !Number.isNaN(Number(mapCnt))
                          ? Number(mapCnt).toLocaleString()
                          : '—'}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeAt(index)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="제거"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductMappedOptionGroupsTab;
